/* eslint-disable max-len */
import React, { useState, useMemo } from 'react';
import { Box, Typography, Stack, Slider, Button } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { InfoOutlined, TrendingDown, AccountBalance, Calculate } from '@mui/icons-material';
import { CustomTooltip } from '../CustomTooltip/CustomTooltip';
import useIsMobile from '../../hooks/useIsMobile';
import {
  estimateBuyingPower,
  describeFeeComparison,
  FEE_ESTIMATE_ASSUMPTIONS,
} from '../../utils/buyerBrokerFee';

interface MLSPropertySavingsProps {
  property: any;
  showCalculatorByDefault?: boolean;
  embedded?: boolean;
}

const MLSPropertySavings: React.FC<MLSPropertySavingsProps> = ({
  property,
  showCalculatorByDefault = false,
  embedded = false,
}) => {
  const isMobile = useIsMobile();
  const { price, listingPrice } = property;
  const [showCalculator, setShowCalculator] = useState(showCalculatorByDefault);

  // Use listingPrice if price is not set (for MLS listings)
  const propertyPrice = price || listingPrice || 0;

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Buying power: a typical 3% commission less Homepoint's estimated fee on
  // this home (1.5%, $5,000 minimum, mirroring the backend's pricing).
  const feeEstimate = estimateBuyingPower(propertyPrice);
  const { typicalCommission, homepointFee } = feeEstimate;
  // Signed so the below-threshold branch can say how much more the minimum is.
  const homepointSavings = typicalCommission - homepointFee;
  const showHomepointSavings = propertyPrice > 0;

  // Savings allocation state (percentage for rate buydown, rest goes to closing costs/price reduction)
  const [rateBuydownPercent, setRateBuydownPercent] = useState(0);

  // Calculate allocations based on slider
  const allocations = useMemo(() => {
    const forRateBuydown = homepointSavings * (rateBuydownPercent / 100);
    const forOther = homepointSavings - forRateBuydown;

    // Calculate rate buydown metrics
    const assumedLoanAmount = propertyPrice * 0.8; // 20% down
    const pointsAvailable = assumedLoanAmount > 0 ? forRateBuydown / (assumedLoanAmount * 0.01) : 0;
    const rateReduction = pointsAvailable > 0 ? Math.min(pointsAvailable * 0.25, 2.0) : 0;
    const monthlyPaymentSavings = rateReduction > 0
      ? Math.round((rateReduction / 0.25) * 15 * (assumedLoanAmount / 100000))
      : 0;
    const lifetimeSavings = monthlyPaymentSavings * 12 * 30;

    return {
      forRateBuydown,
      forOther,
      assumedLoanAmount,
      rateReduction,
      monthlyPaymentSavings,
      lifetimeSavings,
    };
  }, [homepointSavings, rateBuydownPercent, propertyPrice]);

  // Homepoint Savings
  if (showHomepointSavings && homepointSavings > 0) {
    // Simplified view - shows value without calculator
    if (!showCalculator && !embedded) {
      return (
        <Box
          sx={{
            bgcolor: '#f8f9fa',
            p: isMobile ? 2 : 2.5,
            width: '100%',
            textAlign: 'center',
          }}
        >
          {/* Commission Savings Display */}
          <Box
            sx={{
              borderRadius: 2,
              p: 2.5,
              bgcolor: 'white',
              border: '1px solid #e5e7eb',
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}
            >
              Your Buying Power
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 800, lineHeight: 1, mb: 1, color: '#27ae60' }}
            >
              {formatCurrency(homepointSavings)}
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
              {describeFeeComparison(feeEstimate)}
            </Typography>

            <Button
              variant="outlined"
              startIcon={<Calculate />}
              onClick={() => setShowCalculator(true)}
              sx={{
                color: '#27ae60',
                borderColor: '#27ae60',
                '&:hover': {
                  borderColor: '#219653',
                  bgcolor: 'rgba(39, 174, 96, 0.04)',
                },
              }}
            >
              Calculate how to use this savings
            </Button>
          </Box>

          {/* Quick tips */}
          <Stack spacing={0.5} sx={{ textAlign: 'left' }}>
            <Typography variant="caption" sx={{ color: '#6b7280' }}>
              Use your savings for:
            </Typography>
            <Typography variant="caption" sx={{ color: '#374151' }}>
              • Buy down your interest rate
            </Typography>
            <Typography variant="caption" sx={{ color: '#374151' }}>
              • Cover closing costs
            </Typography>
            <Typography variant="caption" sx={{ color: '#374151' }}>
              • Reduce purchase price
            </Typography>
          </Stack>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          bgcolor: '#f8f9fa',
          p: isMobile ? 2 : 2.25,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ mb: 2 }}>
          {/* Commission Savings Display */}
          <Box
            sx={{
              borderRadius: 2,
              p: 2,
              textAlign: 'center',
              mb: 2,
              bgcolor: 'white',
              border: '1px solid #e5e7eb',
            }}
          >
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              justifyContent="center"
              mb={1}
            >
              <Typography
                variant="body2"
                sx={{ color: '#6b7280', fontWeight: 500 }}
              >
                Your Buying Power
              </Typography>
              <CustomTooltip
                title={`Compared with a typical 3% buyer's agent commission. ${FEE_ESTIMATE_ASSUMPTIONS}`}
                placement="top"
                width="280px"
              >
                <InfoOutlined sx={{ fontSize: 14, color: '#9ca3af' }} />
              </CustomTooltip>
            </Stack>
            <Typography
              variant="h3"
              sx={{ fontWeight: 800, lineHeight: 1, mb: 1, color: '#27ae60' }}
            >
              {formatCurrency(homepointSavings)}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                Typical 3%: {formatCurrency(typicalCommission)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#d1d5db' }}>
                →
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: '#6b7280', fontWeight: 600 }}
              >
                Homepoint: {formatCurrency(homepointFee)}
              </Typography>
            </Box>
          </Box>

          {/* Savings Allocation Calculator */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: 'white',
              border: '1px solid #e5e7eb',
            }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{ color: '#374151', fontWeight: 600 }}
              >
                How would you use your savings?
              </Typography>
              <CustomTooltip
                title="Drag the slider to allocate your savings between lowering your mortgage rate or reducing upfront costs."
                placement="top"
                width="280px"
              >
                <InfoOutlined sx={{ fontSize: 14, color: '#9ca3af' }} />
              </CustomTooltip>
            </Stack>

            {/* Slider */}
            <Box sx={{ px: 1, mb: 2 }}>
              <Slider
                value={rateBuydownPercent}
                onChange={(_, value) => setRateBuydownPercent(value as number)}
                min={0}
                max={100}
                step={10}
                sx={{
                  color: '#27ae60',
                  '& .MuiSlider-thumb': {
                    backgroundColor: '#27ae60',
                    '&:hover, &.Mui-focusVisible': {
                      boxShadow: '0 0 0 8px rgba(39, 174, 96, 0.16)',
                    },
                  },
                  '& .MuiSlider-track': {
                    backgroundColor: '#27ae60',
                  },
                  '& .MuiSlider-rail': {
                    backgroundColor: '#279AF1',
                    opacity: 0.3,
                  },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -0.5 }}>
                <Typography variant="caption" sx={{ color: '#279AF1', fontWeight: 500 }}>
                  Closing Costs
                </Typography>
                <Typography variant="caption" sx={{ color: '#27ae60', fontWeight: 500 }}>
                  Rate Buydown
                </Typography>
              </Box>
            </Box>

            {/* Allocation Cards */}
            <Grid2 container spacing={1.5}>
              {/* Rate Buydown Card */}
              <Grid2 xs={6}>
                <Box
                  sx={{
                    bgcolor: rateBuydownPercent > 0 ? '#f0fdf4' : '#f9fafb',
                    border: rateBuydownPercent > 0 ? '1px solid #bbf7d0' : '1px solid #e5e7eb',
                    p: 1.5,
                    borderRadius: 1.5,
                    height: '100%',
                  }}
                >
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
                    <TrendingDown sx={{ fontSize: 16, color: '#27ae60' }} />
                    <Typography
                      variant="caption"
                      sx={{ color: '#374151', fontWeight: 600 }}
                    >
                      Rate Buydown
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: '#27ae60', mb: 0.5 }}
                  >
                    {formatCurrency(allocations.forRateBuydown)}
                  </Typography>
                  {allocations.rateReduction > 0 && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e5e7eb' }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                        Rate reduction: <strong style={{ color: '#27ae60' }}>-{allocations.rateReduction.toFixed(2)}%</strong>
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                        Monthly savings: <strong>~{formatCurrency(allocations.monthlyPaymentSavings)}</strong>
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                        30-year total: <strong>~{formatCurrency(allocations.lifetimeSavings)}</strong>
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid2>

              {/* Closing Costs / Other Card */}
              <Grid2 xs={6}>
                <Box
                  sx={{
                    bgcolor: allocations.forOther > 0 ? '#eff6ff' : '#f9fafb',
                    border: allocations.forOther > 0 ? '1px solid #bfdbfe' : '1px solid #e5e7eb',
                    p: 1.5,
                    borderRadius: 1.5,
                    height: '100%',
                  }}
                >
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
                    <AccountBalance sx={{ fontSize: 16, color: '#279AF1' }} />
                    <Typography
                      variant="caption"
                      sx={{ color: '#374151', fontWeight: 600 }}
                    >
                      Closing Costs
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: '#279AF1', mb: 0.5 }}
                  >
                    {formatCurrency(allocations.forOther)}
                  </Typography>
                  {allocations.forOther > 0 && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e5e7eb' }}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                        Use for:
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#374151', display: 'block' }}>
                        - Cover closing costs
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#374151', display: 'block' }}>
                        - Reduce purchase price
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#374151', display: 'block' }}>
                        - Strengthen your offer
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid2>
            </Grid2>
          </Box>
        </Box>

        {/* Disclaimer */}
        <Box sx={{ mt: 'auto', pt: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontSize: 10,
              color: '#9ca3af',
              fontStyle: 'italic',
              borderTop: '1px solid #e5e7eb',
              pt: 1,
            }}
          >
            *Estimates based on {formatCurrency(allocations.assumedLoanAmount)} loan (20%
            down), 30-year fixed. Actual rate buydown and savings will vary by
            lender and market conditions.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Transparent pricing message
  if (showHomepointSavings && homepointSavings <= 0) {
    return (
      <Box
        sx={{
          bgcolor: '#f3f4f6',
          p: 1.75,
          borderRadius: 2,
          mx: 3,
          my: 2,
        }}
      >
        <Typography
          sx={{ fontSize: 14, fontWeight: 600, color: '#374151', mb: 1 }}
        >
          Homepoint Transparent Pricing
        </Typography>
        <Typography sx={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
          For this property, Homepoint&apos;s {formatCurrency(homepointFee)}{' '}
          minimum fee is{' '}
          {homepointSavings < 0
            ? `${formatCurrency(Math.abs(homepointSavings))} more than`
            : 'equal to'}{' '}
          the typical {formatCurrency(typicalCommission)} (3%) commission.
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            color: '#9ca3af',
            mt: 1,
            fontStyle: 'italic',
          }}
        >
          {FEE_ESTIMATE_ASSUMPTIONS} The buying power grows with the price.
        </Typography>
      </Box>
    );
  }

  // Default message when no savings available
  return (
    <Box sx={{ textAlign: 'center', px: 3, py: 4 }}>
      <Typography sx={{ fontSize: 16, color: '#6b7280', mb: 2 }}>
        Schedule a showing to learn more about this property
      </Typography>
      <Typography sx={{ fontSize: 14, color: '#9ca3af' }}>
        Our expert agents can help you explore financing options
      </Typography>
    </Box>
  );
};

export default MLSPropertySavings;
